import React from "react";
import { gql, useMutation } from "@apollo/client";
import {
  VerifyNinMutationMutation,
  VerifyNinMutationMutationVariables
} from "../../schemaTypes";

export const VERIFY_NIN_MUTATION = gql`
  mutation VerifyNinMutation(
    $nin: String!
    $firstname: String!
    $lastname: String!
  ) {
    verifyNIN(
      nin: $nin, 
      firstname: $firstname, 
      lastname: $lastname
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

export interface WithVerifyNIN {
  verifyNIN: (
    variables: VerifyNinMutationMutationVariables
  ) => Promise<{
    errors: Record<string, string> | null;
    message?: string | null;
    supportMeta?: {
      code?: string;
      supportRef?: string;
    };
  }>;
}

function normalizeErrors(
  errors: { path: string; message: string }[] | null | undefined
): Record<string, string> | null {
  if (!errors || errors.length === 0) return null;
  const errMap: Record<string, string> = {};
  errors.forEach(e => {
    if (e && e.path) {
      errMap[e.path] = e.message;
    }
  });
  return errMap;
}

export function useVerifyNIN(): WithVerifyNIN {
  const [mutate] = useMutation<
    VerifyNinMutationMutation,
    VerifyNinMutationMutationVariables
  >(VERIFY_NIN_MUTATION);

  const verifyNIN = React.useCallback(async (
    variables: VerifyNinMutationMutationVariables
  ) => {
    try {
      const response = await mutate({ variables });
      const result = response?.data?.verifyNIN;
      if (!result) {
        return {
          errors: { general: "No response from server." },
          message: undefined,
          supportMeta: undefined,
        };
      }
      const errors = normalizeErrors(result.errors);
      let message = result.message;
      
      return { errors, message, supportMeta: undefined };
    } catch (err) {
      const maybeApolloErr = err as {
        message?: string;
        graphQLErrors?: Array<{ message?: string; extensions?: { code?: string; supportRef?: string } }>;
      };
      const firstGraphQLError = maybeApolloErr.graphQLErrors?.[0];
      const supportRef = firstGraphQLError?.extensions?.supportRef;
      const code = firstGraphQLError?.extensions?.code;
      const message =
        firstGraphQLError?.message ||
        maybeApolloErr.message ||
        "Something went wrong while verifying NIN.";

      return {
        errors: { general: message },
        message: undefined,
        supportMeta: {
          ...(typeof code === "string" ? { code } : {}),
          ...(typeof supportRef === "string" ? { supportRef } : {}),
        },
      };
    }
  }, [mutate]);

  return { verifyNIN };
}
